import { Box, Typography } from '@veraclins-dev/ui';
import { cn } from '@veraclins-dev/utils';
import type { ReactNode } from 'react';

import { site } from '../content/site';
import { AppExternalLink } from '../link';

import { EditorialExternalLink, EditorialLede } from './editorial';

export interface FooterProps {
  className?: string;
  headline?: ReactNode;
  lede?: ReactNode;
  ctaLabel?: string;
  ctaHref?: string;
}

const links = [
  { label: 'Email', href: `mailto:${site.contact.email}` },
  { label: 'GitHub', href: site.social.github },
  { label: 'LinkedIn', href: site.social.linkedin },
] as const;

export function Footer({
  className,
  headline,
  lede,
  ctaLabel = 'Book a call',
  ctaHref = 'https://cal.com/clintonagada',
}: FooterProps) {
  const hasCallout = Boolean(headline || lede);

  return (
    <Box
      component="footer"
      borderTop={1}
      mt={10}
      pt={hasCallout ? undefined : 8}
      pb={8}
      // oxlint-disable-next-line veraclins-ui/prefer-style-props -- primary-tinted border color and conditional clamp() spacing have no scale-token/prop equivalent
      className={cn(
        'border-primary/12',
        hasCallout && 'pt-[clamp(3rem,6vw,4.5rem)]',
        className,
      )}
    >
      {hasCallout ? (
        <Box
          display="flex"
          flexDirection="column"
          items="start"
          gap={4}
          mb={10}
          // oxlint-disable-next-line veraclins-ui/prefer-style-props -- 44ch max-width has no scale-token/prop equivalent
          className="max-w-[44ch]"
        >
          {headline ? (
            <Typography
              variant="h2"
              className="text-balance font-sans text-[clamp(2rem,4vw,3rem)] font-extrabold leading-none tracking-tight"
            >
              {headline}
            </Typography>
          ) : null}
          {lede ? (
            <EditorialLede className="text-lg leading-normal">
              {lede}
            </EditorialLede>
          ) : null}
          <EditorialExternalLink href={ctaHref}>
            {ctaLabel}
          </EditorialExternalLink>
        </Box>
      ) : null}

      <Box
        display="flex"
        flexWrap="wrap"
        items="center"
        justify="between"
        gap={5}
        className="text-sm/normal text-foreground/60"
      >
        <Typography component="span" variant="inherit">
          &copy; 2026 Clinton Agada · Built with care in Abuja
        </Typography>
        <Box display="flex" className="gap-5.5">
          {links.map((link) => (
            <AppExternalLink
              key={link.label}
              href={link.href}
              underline="none"
              tone="neutral"
              className="text-foreground/60 transition hover:text-foreground"
            >
              {link.label}
            </AppExternalLink>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
